import { Download, Tag } from "lucide-react";
import { Reveal } from "./Reveal";

const versions = [
  {
    id: "v120",
    version: "v1.2.0-beta",
    date: "July 2026",
    tag: "Latest desktop build",
    highlights: ["AI auto-categorization engine", "Dark mode UI polish", "Export CSV & PDF reports"],
  },
  {
    id: "v112",
    version: "v1.1.2",
    date: "May 2026",
    tag: "Stable",
    highlights: ["Faster local storage engine", "Multi-currency support", "Custom budget targets"],
  },
  {
    id: "v100",
    version: "v1.0.0",
    date: "March 2026",
    tag: "Initial beta",
    highlights: ["Core income & expense tracker", "Basic trend graphs", "Cross-platform desktop engine"],
  },
];

export const Changelog = ({ onNotify }) => (
  <section
    id="versions"
    className="border-t border-sky-400/10 bg-[#0F1C36]/30 py-28 lg:py-36"
    data-testid="changelog-section"
  >
    <div className="mx-auto max-w-7xl px-6 lg:px-10">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <Reveal>
          <p className="font-mono-num text-xs uppercase tracking-[0.3em] text-sky-400">Version history</p>
          <h2 className="font-serif-display mt-4 max-w-xl text-2xl tracking-tight text-slate-100 sm:text-3xl lg:text-4xl">
            Desktop releases, in the open.
          </h2>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="max-w-sm text-sm leading-relaxed text-slate-500">
            Every new desktop build gets posted here as it ships. Download links activate with the
            public launch.
          </p>
        </Reveal>
      </div>

      <div className="relative mt-16 space-y-6 before:absolute before:left-[7px] before:top-2 before:h-full before:w-px before:bg-gradient-to-b before:from-sky-400/40 before:to-transparent">
        {versions.map((v, i) => (
          <Reveal key={v.id} delay={0.08 * i}>
            <article
              className="group relative ml-8 rounded-3xl border border-sky-400/10 bg-[#132342]/40 p-7 transition-all duration-500 hover:border-sky-400/35 hover:bg-[#1B2F56]/40 sm:p-8"
              data-testid={`version-card-${v.id}`}
            >
              <span
                className={`absolute -left-8 top-9 h-3.5 w-3.5 rounded-full border-4 border-[#0A1224] ${
                  i === 0 ? "bg-sky-400 animate-pulse-dot" : "bg-slate-600"
                }`}
              />
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex flex-wrap items-center gap-4">
                  <h3 className="font-mono-num text-xl font-medium text-slate-50">{v.version}</h3>
                  <span className="flex items-center gap-1.5 rounded-full border border-sky-400/25 bg-sky-400/10 px-3 py-1 text-[11px] uppercase tracking-[0.15em] text-sky-300">
                    <Tag size={11} />
                    {v.tag}
                  </span>
                </div>
                <span className="font-mono-num text-xs uppercase tracking-[0.2em] text-slate-500">{v.date}</span>
              </div>
              <ul className="mt-5 grid gap-2 sm:grid-cols-3">
                {v.highlights.map((h) => (
                  <li key={h} className="flex items-start gap-2 text-sm text-slate-400">
                    <span className="mt-1.5 h-1 w-1 shrink-0 rotate-45 bg-sky-400/70" />
                    {h}
                  </li>
                ))}
              </ul>
              <button
                data-testid={`version-download-${v.id}-btn`}
                onClick={() =>
                  onNotify("Coming soon", `${v.version} will be downloadable here once public releases open.`)
                }
                className="mt-6 inline-flex items-center gap-2 rounded-full border border-sky-400/20 px-5 py-2 text-sm text-sky-300 transition-all duration-300 hover:border-sky-400/50 hover:bg-sky-400/10"
              >
                <Download size={15} />
                Download build
              </button>
            </article>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);
